import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useActiveCompany } from '@/lib/active-company-context';
import { useApi } from '@/lib/api-context';

import {
  annulResponseSchema,
  lmsLinkResponseSchema,
  lmsSearchResponseSchema,
  ownerResponseSchema,
  registeredEntryResponseSchema,
  retryResponseSchema,
  type LmsSearchRow,
  type LmsSyncKind,
  type RegisteredEntry,
  type RegisterInput,
} from './types';

// Datos del alta de documento: alta con reserva, corrección de código, anulación y
// vínculo con el documento del LMS. Cada mutación refresca la lista y la ficha.

const BASE = '/api/v1/quality/documents';

function useRefreshDocuments(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['master-list'] }),
      qc.invalidateQueries({ queryKey: ['dossier'] }),
      // El editor ofrece acciones según el estado: tras anular no debe ofrecer avanzar.
      qc.invalidateQueries({ queryKey: ['flow-capabilities'] }),
    ]);
  };
}

export function useRegisterDocument(): UseMutationResult<RegisteredEntry, Error, RegisterInput> {
  const api = useApi();
  const refresh = useRefreshDocuments();
  return useMutation({
    mutationFn: async (input) =>
      registeredEntryResponseSchema.parse(await api.post<unknown>(BASE, input)).data,
    onSuccess: refresh,
  });
}

export function useCorrectCode(entryId: string): UseMutationResult<RegisteredEntry, Error, string> {
  const api = useApi();
  const refresh = useRefreshDocuments();
  return useMutation({
    mutationFn: async (code) =>
      registeredEntryResponseSchema.parse(
        await api.patch<unknown>(`${BASE}/${entryId}/code`, { code }),
      ).data,
    onSuccess: refresh,
  });
}

export function useAnnulDocument(entryId: string): UseMutationResult<void, Error, string> {
  const api = useApi();
  const refresh = useRefreshDocuments();
  return useMutation({
    mutationFn: async (reason) => {
      annulResponseSchema.parse(await api.post<unknown>(`${BASE}/${entryId}/annul`, { reason }));
    },
    onSuccess: refresh,
  });
}

export function useLinkLmsDocument(entryId: string): UseMutationResult<void, Error, number> {
  const api = useApi();
  const refresh = useRefreshDocuments();
  return useMutation({
    mutationFn: async (lmsDocumentId) => {
      lmsLinkResponseSchema.parse(
        await api.put<unknown>(`${BASE}/${entryId}/lms-document`, { lmsDocumentId }),
      );
    },
    onSuccess: refresh,
  });
}

/** Dueño de proceso (G3-3): asignarlo, cambiarlo o quitarlo (null). */
export function useSetOwner(entryId: string): UseMutationResult<void, Error, string | null> {
  const api = useApi();
  const refresh = useRefreshDocuments();
  return useMutation({
    mutationFn: async (ownerUserId) => {
      ownerResponseSchema.parse(
        await api.put<unknown>(`${BASE}/${entryId}/owner`, { ownerUserId }),
      );
    },
    onSuccess: refresh,
  });
}

/** Reintento manual de un envío al DMS del LMS que el sistema ya no reintenta (G3). */
export function useRetryLmsSync(entryId: string): UseMutationResult<void, Error, LmsSyncKind> {
  const api = useApi();
  const refresh = useRefreshDocuments();
  return useMutation({
    mutationFn: async (kind) => {
      retryResponseSchema.parse(
        await api.post<unknown>(`${BASE}/${entryId}/lms-sync/${kind}/retry`, {}),
      );
    },
    onSuccess: refresh,
  });
}

/** Búsqueda de documentos del LMS de la empresa activa (desde 2 caracteres). */
export function useLmsSearch(term: string): UseQueryResult<LmsSearchRow[]> {
  const api = useApi();
  const { activeCompanyId } = useActiveCompany();
  const q = term.trim();
  return useQuery({
    queryKey: ['lms-documents', activeCompanyId, q],
    queryFn: async () =>
      lmsSearchResponseSchema.parse(
        await api.get<unknown>(`/api/v1/quality/lms-documents?q=${encodeURIComponent(q)}`),
      ).data,
    enabled: q.length >= 2 && Boolean(activeCompanyId),
    // Siempre fresca: Calidad crea el documento en el LMS y lo busca enseguida.
    staleTime: 0,
  });
}
